/**
 * Process-wide cache of the Supabase auth session.
 *
 * Why this exists: every `supabase.auth.getSession()` call grabs the gotrue
 * navigator-lock. When two callers race, the second one steals the lock and
 * aborts the first — surfacing in user code as:
 *   "AbortError: Lock broken by another request with the 'steal' option"
 * and as a gotrue warning:
 *   "Lock 'lock:sb-...-auth-token' was not released within 5000ms"
 *
 * Both were the dominant source of error-log volume (~150+ occurrences in
 * a typical 48h window). The structural fix: maintain a single in-memory
 * mirror of the session, populated once by an explicit `getSession()` on
 * boot and kept in sync via `onAuthStateChange`. Subsequent reads are
 * synchronous, lock-free, and never abort other callers.
 *
 * Anyone who previously called `supabase.auth.getSession()` solely to read
 * a JWT or user email should call `getCachedSession()` instead.
 */
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';

let cachedSession: Session | null = null;
let bootstrapPromise: Promise<Session | null> | null = null;
let subscribed = false;

/** Wire the cache to `onAuthStateChange`. Idempotent. */
const ensureSubscribed = (): void => {
    if (subscribed) return;
    subscribed = true;
    supabase.auth.onAuthStateChange((_event, newSession) => {
        cachedSession = newSession;
    });
};

/**
 * Initialise the cache on app boot. Returns the first observed session.
 * Calling multiple times is safe — only the first call hits gotrue.
 *
 * This MUST run once before any synchronous `getCachedSession()` reader can
 * trust its return value. AuthProvider calls this on mount; do not call
 * `getSession()` directly outside of bootstrap paths.
 */
export const initSessionCache = async (): Promise<Session | null> => {
    ensureSubscribed();
    if (bootstrapPromise) return bootstrapPromise;
    bootstrapPromise = supabase.auth.getSession().then(({ data }) => {
        cachedSession = data.session;
        return cachedSession;
    }).catch(() => {
        // If bootstrap fails, keep cache null but allow a retry next call.
        bootstrapPromise = null;
        return null;
    });
    return bootstrapPromise;
};

/** Synchronous lock-free read. Returns null before bootstrap completes. */
export const getCachedSession = (): Session | null => cachedSession;

/** Async accessor. Awaits bootstrap on first call, then returns the cache.
 *  Never grabs the gotrue lock after the first call. */
export const getSessionOrBootstrap = async (): Promise<Session | null> => {
    if (cachedSession) return cachedSession;
    if (bootstrapPromise) return bootstrapPromise;
    return initSessionCache();
};

/** Convenience: just the email, or null. */
export const getCachedEmail = (): string | null => cachedSession?.user?.email ?? null;

/** Convenience: just the JWT, or null. */
export const getCachedAccessToken = (): string | null => cachedSession?.access_token ?? null;
